import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, type FieldErrors } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Info, LockSimple, SpinnerGap, WarningCircle, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { Input } from '@/components/ui-kit/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui-kit/sheet';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { UIPhoneInput } from '@/components/core';
import { CustomFieldInput } from '../custom-field-input/custom-field-input';
import { useCustomFieldDefinitions } from '../../hooks/use-custom-field-definitions';
import { useGetLists } from '@/modules/lists/hooks/use-lists';
import {
  useInsertContact,
  useUpdateContact,
  useUpdateContactSubscriptions,
} from '../../hooks/use-contacts';
import type { Contact } from '../../types/contacts.types';
import { getContactsFromRest } from '../../services/contacts-rest.service';
import { mapContactApiFieldErrors } from '../../utils/contact-field-errors';
import { formatContactSourceLabel } from '../../utils/contact-source.utils';
import { getEditableCustomFieldDefinitions } from '../../utils/custom-field-attributes.utils';
import {
  buildCreateContactRestRequest,
  buildUpdateContactRestRequest,
  buildUpdateSubscriptionRestRequest,
  CONTACT_DRAWER_FORM_DEFAULTS,
  CONTACT_LOCALE_OPTIONS,
  createContactDrawerFormSchema,
  CONTACT_NAME_MAX_LENGTH,
  mapContactToDrawerFormValues,
  SUBSCRIPTION_OPTIONS,
  type ContactDrawerFormValues,
} from '../../utils/contact-drawer-form.utils';

type ContactDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact?: Contact | null;
};

const DrawerSection = ({
  title,
  children,
  className,
}: {
  title?: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={cn('border-t border-neutral-200 pt-5 first:border-t-0 first:pt-0', className)}>
    {title ? (
      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-neutral-500">
        {title}
      </div>
    ) : null}
    {children}
  </div>
);

const serializeFormValues = (values: ContactDrawerFormValues): string => JSON.stringify(values);

export const ContactDrawer = ({ open, onOpenChange, contact }: ContactDrawerProps) => {
  const { toast } = useToast();
  const isEditMode = Boolean(contact?.ItemId);
  const [showDiscardBanner, setShowDiscardBanner] = useState(false);
  const [showServerError, setShowServerError] = useState(false);
  const [showDuplicateNotice, setShowDuplicateNotice] = useState(false);
  const initialSnapshotRef = useRef<string | null>(null);

  const { data: customFieldDefinitionsData } = useCustomFieldDefinitions();
  const { data: listsData } = useGetLists({ pageNo: 1, pageSize: 200 }, { enabled: open });

  const customFieldDefinitions = useMemo(
    () => customFieldDefinitionsData?.items ?? [],
    [customFieldDefinitionsData?.items]
  );
  const editableCustomFields = useMemo(
    () => getEditableCustomFieldDefinitions(customFieldDefinitions),
    [customFieldDefinitions]
  );
  const listOptions = useMemo(
    () =>
      (listsData?.items ?? [])
        .map((list) => ({
          id: list.id,
          name: list.name.trim(),
        }))
        .filter((list) => list.id && list.name),
    [listsData?.items]
  );

  const { mutateAsync: addContact, isPending: isInserting } = useInsertContact();
  const { mutateAsync: updateContact, isPending: isUpdating } = useUpdateContact();
  const { mutateAsync: updateSubscriptions, isPending: isUpdatingSubscriptions } =
    useUpdateContactSubscriptions();
  const isPending = isInserting || isUpdating || isUpdatingSubscriptions;

  const formSchema = useMemo(
    () => createContactDrawerFormSchema(customFieldDefinitions),
    [customFieldDefinitions]
  );

  const form = useForm<ContactDrawerFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: CONTACT_DRAWER_FORM_DEFAULTS,
    shouldFocusError: true,
    mode: 'onChange',
  });

  const prevOpenRef = useRef(false);
  const resetForm = useCallback(() => {
    const lists = listsData?.items ?? [];
    const values = contact
      ? mapContactToDrawerFormValues(contact, customFieldDefinitions, lists)
      : CONTACT_DRAWER_FORM_DEFAULTS;

    form.reset(values);
    initialSnapshotRef.current = serializeFormValues(values);
    setShowDiscardBanner(false);
    setShowServerError(false);
    setShowDuplicateNotice(false);
  }, [contact, listsData?.items, customFieldDefinitions, form]);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      resetForm();
    }
    prevOpenRef.current = open;
  }, [open, resetForm]);

  useEffect(() => {
    if (!open || editableCustomFields.length === 0) return;

    const currentCustomFields = form.getValues('customFields') ?? {};
    let changed = false;
    const nextCustomFields = { ...currentCustomFields };

    editableCustomFields.forEach((definition) => {
      if (nextCustomFields[definition.Key] === undefined) {
        nextCustomFields[definition.Key] = '';
        changed = true;
      }
    });

    if (changed) {
      form.setValue('customFields', nextCustomFields, { shouldValidate: false });
    }
  }, [open, editableCustomFields, form]);

  const requestClose = () => {
    const currentSnapshot = serializeFormValues(form.getValues());
    if (initialSnapshotRef.current !== currentSnapshot) {
      setShowDiscardBanner(true);
      return;
    }
    onOpenChange(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      requestClose();
      return;
    }
    onOpenChange(true);
  };

  const handleInvalid = (errors: FieldErrors<ContactDrawerFormValues>) => {
    const firstCustomFieldError = Object.entries(errors.customFields ?? {}).find(
      ([, fieldError]) => fieldError?.message
    );
    const firstMessage =
      errors.email?.message ??
      errors.phone?.message ??
      errors.firstName?.message ??
      errors.lastName?.message ??
      firstCustomFieldError?.[1]?.message;

    toast({
      variant: 'destructive',
      title: 'Please fix the highlighted fields',
      description: firstMessage ? String(firstMessage) : 'Please complete all required fields.',
    });

    if (errors.email) {
      form.setFocus('email');
    } else if (errors.phone) {
      form.setFocus('phone');
    } else if (errors.firstName) {
      form.setFocus('firstName');
    } else if (errors.lastName) {
      form.setFocus('lastName');
    } else if (firstCustomFieldError) {
      // Radio/select controls are not focusable by name, so scroll the field's message into view.
      requestAnimationFrame(() => {
        document
          .querySelector('[data-custom-field-error="true"]')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  };

  const checkDuplicateEmail = async (rawEmail: string) => {
    const email = rawEmail.trim().toLowerCase();
    if (isEditMode || !email || !z.string().email().safeParse(email).success) {
      setShowDuplicateNotice(false);
      return;
    }
    try {
      const result = await getContactsFromRest({ q: email, pageNo: 1, pageSize: 5 });
      setShowDuplicateNotice(
        result.items.some((existing) => existing.Email?.trim().toLowerCase() === email)
      );
    } catch {
      setShowDuplicateNotice(false);
    }
  };

  const handleSubmit = async (values: ContactDrawerFormValues) => {
    setShowServerError(false);

    try {
      if (isEditMode && contact) {
        await updateContact({
          contactId: contact.ItemId,
          input: buildUpdateContactRestRequest(values, customFieldDefinitions),
        });

        const subscriptionUpdate = buildUpdateSubscriptionRestRequest(values, contact);
        if (subscriptionUpdate) {
          await updateSubscriptions({
            contactId: contact.ItemId,
            input: subscriptionUpdate,
          });
        }

        toast({
          variant: 'success',
          title: 'Contact updated',
          description: 'Your changes were saved.',
        });
      } else {
        const result = await addContact(buildCreateContactRestRequest(values, customFieldDefinitions));
        // The backend says whether the address matched an existing contact it merged into.
        toast(
          result.merged
            ? {
                variant: 'success',
                title: 'Contact updated',
                description: 'A contact with this email already existed, so it was updated instead.',
              }
            : {
                variant: 'success',
                title: 'Contact added',
                description: 'The contact was created successfully.',
              }
        );
      }

      onOpenChange(false);
    } catch (error) {
      const { fieldErrors, unmapped } = mapContactApiFieldErrors(
        error,
        editableCustomFields.map((definition) => definition.Key)
      );

      if (fieldErrors.length > 0) {
        fieldErrors.forEach(({ path, message }) => {
          form.setError(path, { type: 'server', message });
        });
        form.setFocus(fieldErrors[0].path, { shouldSelect: true });

        // Only what the form cannot show on a field still needs the toast.
        if (unmapped.length > 0) {
          toast({
            variant: 'destructive',
            title: isEditMode ? 'Failed to update contact' : 'Failed to add contact',
            description: unmapped.join(' '),
          });
        }
        return;
      }

      setShowServerError(true);
      toast({
        variant: 'destructive',
        title: isEditMode ? 'Failed to update contact' : 'Failed to add contact',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  const sourceLabel = formatContactSourceLabel(contact) || '—';
  const sourceClient = contact?.SourceClient?.trim() || '—';

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        overlayClassName="bg-black/40"
        className="flex w-full max-w-[480px] flex-col gap-0 overflow-hidden border-neutral-200 bg-white p-0 shadow-e4 sm:max-w-[480px] [&>button]:hidden"
      >
        <SheetTitle className="sr-only">{isEditMode ? 'Edit contact' : 'Add contact'}</SheetTitle>
        <SheetDescription className="sr-only">
          {isEditMode ? 'Update contact details' : 'Create a new contact'}
        </SheetDescription>

        <div className="flex h-14 shrink-0 items-center justify-between border-b border-neutral-200 px-5">
          <span className="text-base font-semibold text-neutral-900">
            {isEditMode ? 'Edit contact' : 'Add contact'}
          </span>
          <button
            type="button"
            className="inline-flex size-8 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
            aria-label="Close"
            onClick={requestClose}
          >
            <X className="size-[18px]" />
          </button>
        </div>

        {showDiscardBanner ? (
          <div
            className="flex shrink-0 items-center justify-between gap-3 border-b border-neutral-200 px-5 py-3"
            style={{ background: '#FBEFDD' }}
          >
            <span className="text-xs text-neutral-800">Discard unsaved changes?</span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-3 text-[13px]"
                onClick={() => setShowDiscardBanner(false)}
              >
                Keep editing
              </Button>
              <Button
                type="button"
                size="sm"
                className="h-8 bg-destructive px-3 text-[13px] text-white hover:bg-[#E8264F]"
                onClick={() => {
                  setShowDiscardBanner(false);
                  onOpenChange(false);
                }}
              >
                Discard
              </Button>
            </div>
          </div>
        ) : null}

        <Form {...form}>
          <form
            noValidate
            onSubmit={form.handleSubmit(handleSubmit, handleInvalid)}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
              <DrawerSection>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[13px] font-medium text-neutral-900">
                        Email
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          inputMode="email"
                          placeholder="jane@example.com"
                          autoComplete="email"
                          className="h-10 border-neutral-300"
                          {...field}
                          onBlur={(event) => {
                            field.onBlur();
                            void checkDuplicateEmail(event.target.value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem className="mt-3">
                      <FormLabel className="text-[13px] font-medium text-neutral-900">
                        Phone
                      </FormLabel>
                      <FormControl>
                        <UIPhoneInput
                          value={field.value || undefined}
                          onChange={(value?: string) => field.onChange(value ?? '')}
                          onBlur={field.onBlur}
                          placeholder="+41 79 555 0142"
                          defaultCountry="CH"
                          countryCallingCodeEditable={false}
                          international
                          className="h-10 border-neutral-300 px-3 py-1 text-sm shadow-sm"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[13px] font-medium text-neutral-900">
                          First name <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            className="h-10 border-neutral-300"
                            autoComplete="given-name"
                            maxLength={CONTACT_NAME_MAX_LENGTH}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[13px] font-medium text-neutral-900">
                          Last name <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            className="h-10 border-neutral-300"
                            autoComplete="family-name"
                            maxLength={CONTACT_NAME_MAX_LENGTH}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="locale"
                  render={({ field }) => (
                    <FormItem className="mt-3">
                      <FormLabel className="text-[13px] font-medium text-neutral-900">
                        Locale
                      </FormLabel>
                      <Select value={field.value || undefined} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="h-10 border-neutral-300">
                            <SelectValue placeholder="Select locale" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CONTACT_LOCALE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {isEditMode ? (
                  <>
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <FormItem>
                        <FormLabel className="text-[13px] font-medium text-neutral-900">
                          Source
                        </FormLabel>
                        <Input className="h-10 border-neutral-300" value={sourceLabel} disabled />
                      </FormItem>
                      <FormItem>
                        <FormLabel className="text-[13px] font-medium text-neutral-900">
                          Source client
                        </FormLabel>
                        <Input className="h-10 border-neutral-300" value={sourceClient} disabled />
                      </FormItem>
                    </div>
                    <p className="mt-2 flex items-center gap-1 text-xs text-neutral-600">
                      <LockSimple className="size-3.5" aria-hidden />
                      Set by the system when the contact was created — not editable.
                    </p>
                  </>
                ) : null}
              </DrawerSection>

              {editableCustomFields.length > 0 ? (
                <DrawerSection title="Custom attributes">
                  {editableCustomFields.map((definition) => (
                    <div key={definition.Key} className="mb-3">
                      <CustomFieldInput
                        control={form.control}
                        definition={definition}
                        name={`customFields.${definition.Key}`}
                      />
                    </div>
                  ))}
                </DrawerSection>
              ) : null}

              <DrawerSection title="Subscriptions">
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    control={form.control}
                    name="subEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[13px] font-medium text-neutral-900">
                          Email
                        </FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="h-10 border-neutral-300">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {SUBSCRIPTION_OPTIONS.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="subSms"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[13px] font-medium text-neutral-900">
                          SMS
                        </FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="h-10 border-neutral-300">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {SUBSCRIPTION_OPTIONS.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                </div>
                <p className="mt-2 text-xs text-neutral-600">
                  Saving here logs a consent event (source: you, timestamp: now) to the
                  contact&apos;s activity timeline — make sure you have permission to contact them.
                </p>
              </DrawerSection>

              {!isEditMode && listOptions.length > 0 ? (
                <DrawerSection title="Lists">
                  <FormField
                    control={form.control}
                    name="listIds"
                    render={({ field }) => (
                      <div className="flex flex-col gap-2">
                        {listOptions.map((list) => {
                          const checked = field.value.includes(list.id);
                          return (
                            <label
                              key={list.id}
                              className="flex cursor-pointer items-center gap-2 text-[13px] text-neutral-900"
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(nextChecked) => {
                                  if (nextChecked === true) {
                                    field.onChange([...field.value, list.id]);
                                    return;
                                  }
                                  field.onChange(field.value.filter((id) => id !== list.id));
                                }}
                              />
                              {list.name}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  />
                </DrawerSection>
              ) : null}

              {isEditMode ? (
                <p className="text-xs text-neutral-600">
                  List membership changes are applied immediately via the list-membership API — not
                  part of this Save.
                </p>
              ) : null}

              {showDuplicateNotice ? (
                <div className="flex items-start gap-2.5 rounded-lg bg-primary-50 px-3 py-2.5">
                  <Info className="mt-0.5 size-4 shrink-0 text-primary-600" aria-hidden />
                  <p className="text-xs text-primary-900">
                    A contact with this email already exists. Saving will update that contact
                    instead of creating a new one.
                  </p>
                </div>
              ) : null}

              {showServerError ? (
                <div className="flex items-start gap-2.5 rounded-lg bg-red-50 px-3 py-2.5">
                  <WarningCircle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
                  <p className="text-xs text-red-900">
                    Something went wrong saving this contact. Your changes have not been lost — try
                    Save again.
                  </p>
                </div>
              ) : null}
            </div>

            <div className="flex shrink-0 items-center justify-end gap-2 border-t border-neutral-200 px-5 py-4">
              <Button
                type="button"
                variant="ghost"
                className="h-10 px-4 text-neutral-900 hover:bg-neutral-100"
                disabled={isPending}
                onClick={requestClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="h-10 bg-primary-600 px-4 text-white hover:bg-primary-700"
                disabled={isPending || !form.formState.isValid}
              >
                {isPending ? (
                  <>
                    <SpinnerGap className="mr-2 size-4 animate-spin" aria-hidden />
                    Saving…
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};

/** @deprecated Use ContactDrawer */
export const AddManualContactModal = ContactDrawer;
